import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import { CodeBlockProps, DocsTokenKind } from "../interfaces";
import { highlightCode } from "../utils";

const COPIED_FEEDBACK_MS: number = 2000;

/**
 * Colour of each kind of token over the near black background of the block. They are picked for
 * contrast rather than for taste: this gets read on a projector, so nothing lands on a muted tint
 * of the background. `plain` inherits the text colour of the block instead of setting its own.
 */
const TOKEN_CLASS_NAMES: Record<DocsTokenKind, string> = {
  plain: "",
  comment: "text-slate-400",
  string: "text-emerald-300",
  number: "text-amber-300",
  keyword: "text-sky-300",
  property: "text-violet-300",
};

export const CodeBlock: FC<CodeBlockProps> = (props) => {
  const { code, language, syntax } = props;
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState<boolean>(false);

  // YOSHI-10464: the snippet is scanned once per snippet and not once per render. Every endpoint of
  // the reference carries two blocks, so re-tokenising on each render would make a scroll or a
  // language change walk thousands of characters for nothing.
  const tokens = useMemo(() => {
    return highlightCode(code, syntax);
  }, [code, syntax]);

  // YOSHI-10464: the confirmation has to clear itself, and the timeout has to die with the block:
  // navigating away from the page while it is pending would set state on an unmounted component.
  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsCopied(false);
    }, COPIED_FEEDBACK_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCopied]);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
    } catch (error) {
      // The clipboard api is unavailable on an insecure origin and rejects when the user denies
      // permission. Neither is worth an alert: the code stays selectable, so the reader can still
      // copy it by hand.
      console.error("Could not copy the snippet to the clipboard", error);
      setIsCopied(false);
    }
  };

  return (
    <div className="w-full rounded-md border border-gray-200 bg-[#0F172A] overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/10">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          {language}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-300 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
        >
          {isCopied ? (
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ClipboardIcon className="h-4 w-4" aria-hidden="true" />
          )}
          {isCopied ? t("docs.common.copied") : t("docs.common.copy")}
        </button>
      </div>
      {/* The snippet scrolls inside its own box: a long curl call must not widen the page on a
          phone, and wrapping a shell command would make it wrong to copy. */}
      <pre className="overflow-x-auto px-4 py-3 text-xs sm:text-sm leading-relaxed text-gray-100">
        {/* The tokens are rendered as elements and never as html: the code carries `<`, `>` and
            `&`, so building markup out of it would be an injection hole and would also paint the
            snippet wrong. The copy button above still copies `code`, the raw string. */}
        <code className="font-mono whitespace-pre">
          {tokens.map((token, index) =>
            token.kind === "plain" ? (
              <Fragment key={index}>{token.text}</Fragment>
            ) : (
              <span key={index} className={TOKEN_CLASS_NAMES[token.kind]}>
                {token.text}
              </span>
            )
          )}
        </code>
      </pre>
    </div>
  );
};
